// TradingView-Quality Indicator Library
import type { IndicatorConfig, OHLCVData } from '../types'

type IndicatorResult = { time: number; value?: number; [key: string]: any }[]

export function calculateIndicator(config: IndicatorConfig, data: OHLCVData[]): IndicatorResult | null {
  if (data.length === 0) return null

  switch (config.type) {
    // Moving Averages
    case 'SMA': return calculateSMA(data, config.params.length as number)
    case 'EMA': return calculateEMA(data, config.params.length as number)
    case 'WMA': return calculateWMA(data, config.params.length as number)
    case 'DEMA': return calculateDEMA(data, config.params.length as number)
    case 'TEMA': return calculateTEMA(data, config.params.length as number)
    case 'HMA': return calculateHMA(data, config.params.length as number)
    case 'VWMA': return calculateVWMA(data, config.params.length as number)
    
    // Oscillators
    case 'RSI': return calculateRSI(data, config.params.length as number)
    case 'MACD': return calculateMACD(data, config.params)
    case 'STOCH': return calculateStochastic(data, config.params)
    case 'STOCHRSI': return calculateStochRSI(data, config.params)
    case 'CCI': return calculateCCI(data, config.params.length as number)
    case 'MFI': return calculateMFI(data, config.params.length as number)
    case 'WILLR': return calculateWilliamsR(data, config.params.length as number)
    case 'ROC': return calculateROC(data, config.params.length as number)
    case 'MOM': return calculateMomentum(data, config.params.length as number)
    case 'AO': return calculateAwesomeOscillator(data, config.params)
    
    // Volatility
    case 'BB': return calculateBollingerBands(data, config.params)
    case 'ATR': return calculateATR(data, config.params.length as number)
    case 'KC': return calculateKeltnerChannel(data, config.params)
    case 'DC': return calculateDonchianChannel(data, config.params.length as number)
    
    // Volume
    case 'VOL': return calculateVolume(data)
    case 'VWAP': return calculateVWAP(data)
    case 'OBV': return calculateOBV(data)
    case 'ADL': return calculateADL(data)
    case 'CMF': return calculateCMF(data, config.params.length as number)
    
    // Trend
    case 'ADX': return calculateADX(data, config.params.length as number)
    case 'DMI': return calculateDMI(data, config.params.length as number)
    case 'PSAR': return calculateParabolicSAR(data, config.params)
    case 'SUPERTREND': return calculateSuperTrend(data, config.params)
    case 'ICHIMOKU': return calculateIchimoku(data, config.params)
    case 'AROON': return calculateAroon(data, config.params.length as number)
    
    // Support/Resistance
    case 'PIVOTS': return calculatePivots(data)
    case 'FIBO_AUTO': return calculateAutoFib(data, config.params.length as number)
    
    default:
      console.warn(`Unknown indicator type: ${config.type}`)
      return null
  }
}

// === Moving Averages ===

function calculateSMA(data: OHLCVData[], period: number): IndicatorResult {
  const result: IndicatorResult = []
  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) {
      result.push({ time: data[i].time })
      continue
    }
    let sum = 0
    for (let j = 0; j < period; j++) {
      sum += data[i - j].close
    }
    result.push({ time: data[i].time, value: sum / period })
  }
  return result
}

function calculateEMA(data: OHLCVData[], period: number): IndicatorResult {
  const result: IndicatorResult = []
  const k = 2 / (period + 1)
  let ema = data[0].close

  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) {
      // Calculate SMA for initial EMA
      let sum = 0
      for (let j = 0; j <= i; j++) sum += data[j].close
      ema = sum / (i + 1)
      result.push({ time: data[i].time })
    } else {
      ema = data[i].close * k + ema * (1 - k)
      result.push({ time: data[i].time, value: ema })
    }
  }
  return result
}

function calculateWMA(data: OHLCVData[], period: number): IndicatorResult {
  const result: IndicatorResult = []
  const divisor = (period * (period + 1)) / 2

  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) {
      result.push({ time: data[i].time })
      continue
    }
    let sum = 0
    for (let j = 0; j < period; j++) {
      sum += data[i - j].close * (period - j)
    }
    result.push({ time: data[i].time, value: sum / divisor })
  }
  return result
}

function calculateDEMA(data: OHLCVData[], period: number): IndicatorResult {
  const ema1 = calculateEMA(data, period)
  const ema1Values = ema1.map(d => ({ time: d.time, close: d.value || 0, open: 0, high: 0, low: 0, volume: 0 }))
  const ema2 = calculateEMA(ema1Values.filter(d => d.close > 0), period)

  const result: IndicatorResult = []
  for (let i = 0; i < data.length; i++) {
    const e1 = ema1[i]?.value
    const e2 = ema2[i - period + 1]?.value
    if (e1 !== undefined && e2 !== undefined) {
      result.push({ time: data[i].time, value: 2 * e1 - e2 })
    } else {
      result.push({ time: data[i].time })
    }
  }
  return result
}

function calculateTEMA(data: OHLCVData[], period: number): IndicatorResult {
  const ema1 = calculateEMA(data, period)
  const ema1Values = ema1.map(d => ({ time: d.time, close: d.value || 0, open: 0, high: 0, low: 0, volume: 0 }))
  const ema2 = calculateEMA(ema1Values.filter(d => d.close > 0), period)
  const ema2Values = ema2.map(d => ({ time: d.time, close: d.value || 0, open: 0, high: 0, low: 0, volume: 0 }))
  const ema3 = calculateEMA(ema2Values.filter(d => d.close > 0), period)

  const result: IndicatorResult = []
  const offset = (period - 1) * 2
  for (let i = 0; i < data.length; i++) {
    const e1 = ema1[i]?.value
    const e2 = ema2[i - period + 1]?.value
    const e3 = ema3[i - offset]?.value
    if (e1 !== undefined && e2 !== undefined && e3 !== undefined) {
      result.push({ time: data[i].time, value: 3 * e1 - 3 * e2 + e3 })
    } else {
      result.push({ time: data[i].time })
    }
  }
  return result
}

function calculateHMA(data: OHLCVData[], period: number): IndicatorResult {
  const halfPeriod = Math.floor(period / 2)
  const sqrtPeriod = Math.floor(Math.sqrt(period))
  
  const wma1 = calculateWMA(data, halfPeriod)
  const wma2 = calculateWMA(data, period)
  
  const rawHMA: OHLCVData[] = []
  for (let i = 0; i < data.length; i++) {
    if (wma1[i]?.value !== undefined && wma2[i]?.value !== undefined) {
      rawHMA.push({
        time: data[i].time,
        close: 2 * wma1[i].value! - wma2[i].value!,
        open: 0, high: 0, low: 0, volume: 0
      })
    }
  }
  
  return calculateWMA(rawHMA, sqrtPeriod).map((d, i) => ({
    time: data[i + period - 1]?.time || d.time,
    value: d.value
  }))
}

function calculateVWMA(data: OHLCVData[], period: number): IndicatorResult {
  const result: IndicatorResult = []
  
  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) {
      result.push({ time: data[i].time })
      continue
    }
    let sumPV = 0, sumV = 0
    for (let j = 0; j < period; j++) {
      sumPV += data[i - j].close * data[i - j].volume
      sumV += data[i - j].volume
    }
    result.push({ time: data[i].time, value: sumV > 0 ? sumPV / sumV : data[i].close })
  }
  return result
}

// === Oscillators ===

function calculateRSI(data: OHLCVData[], period: number): IndicatorResult {
  const result: IndicatorResult = []
  let avgGain = 0, avgLoss = 0

  for (let i = 0; i < data.length; i++) {
    if (i === 0) {
      result.push({ time: data[i].time })
      continue
    }

    const change = data[i].close - data[i - 1].close
    const gain = change > 0 ? change : 0
    const loss = change < 0 ? -change : 0

    if (i < period) {
      avgGain += gain
      avgLoss += loss
      result.push({ time: data[i].time })
    } else if (i === period) {
      avgGain = (avgGain + gain) / period
      avgLoss = (avgLoss + loss) / period
      const rs = avgLoss === 0 ? 100 : avgGain / avgLoss
      result.push({ time: data[i].time, value: 100 - (100 / (1 + rs)) })
    } else {
      avgGain = (avgGain * (period - 1) + gain) / period
      avgLoss = (avgLoss * (period - 1) + loss) / period
      const rs = avgLoss === 0 ? 100 : avgGain / avgLoss
      result.push({ time: data[i].time, value: 100 - (100 / (1 + rs)) })
    }
  }
  return result
}

function calculateMACD(data: OHLCVData[], params: Record<string, any>): IndicatorResult {
  const fast = params.fastLength || 12
  const slow = params.slowLength || 26
  const signal = params.signalLength || 9
  
  const emaFast = calculateEMA(data, fast)
  const emaSlow = calculateEMA(data, slow)
  
  const macdLine: OHLCVData[] = []
  const result: IndicatorResult = []
  
  for (let i = 0; i < data.length; i++) {
    if (emaFast[i]?.value !== undefined && emaSlow[i]?.value !== undefined) {
      const macd = emaFast[i].value! - emaSlow[i].value!
      macdLine.push({ time: data[i].time, close: macd, open: 0, high: 0, low: 0, volume: 0 })
    }
  }
  
  const signalLine = calculateEMA(macdLine, signal)
  
  for (let i = 0; i < data.length; i++) {
    const macdIdx = i - slow + 1
    if (macdIdx >= 0 && macdLine[macdIdx]) {
      const macdValue = macdLine[macdIdx].close
      const signalValue = signalLine[macdIdx - signal + 1]?.value
      if (signalValue !== undefined) {
        result.push({
          time: data[i].time,
          macd: macdValue,
          signal: signalValue,
          histogram: macdValue - signalValue
        })
      } else {
        result.push({ time: data[i].time, macd: macdValue })
      }
    } else {
      result.push({ time: data[i].time })
    }
  }
  return result
}

function calculateStochastic(data: OHLCVData[], params: Record<string, any>): IndicatorResult {
  const kPeriod = params.kPeriod || 14
  const kSmooth = params.kSmooth || 3
  const dSmooth = params.dSmooth || 3
  
  const result: IndicatorResult = []
  const rawK: number[] = []
  
  for (let i = 0; i < data.length; i++) {
    if (i < kPeriod - 1) {
      result.push({ time: data[i].time })
      continue
    }
    
    let highest = -Infinity, lowest = Infinity
    for (let j = 0; j < kPeriod; j++) {
      highest = Math.max(highest, data[i - j].high)
      lowest = Math.min(lowest, data[i - j].low)
    }
    
    const range = highest - lowest
    rawK.push(range > 0 ? ((data[i].close - lowest) / range) * 100 : 50)
    
    if (rawK.length >= kSmooth) {
      const k = rawK.slice(-kSmooth).reduce((a, b) => a + b, 0) / kSmooth
      result.push({ time: data[i].time, k, d: k })
    } else {
      result.push({ time: data[i].time })
    }
  }
  
  // Calculate %D
  for (let i = dSmooth - 1; i < result.length; i++) {
    if (result[i].k !== undefined) {
      let sum = 0, count = 0
      for (let j = 0; j < dSmooth && i - j >= 0; j++) {
        if (result[i - j].k !== undefined) {
          sum += result[i - j].k
          count++
        }
      }
      if (count === dSmooth) {
        result[i].d = sum / dSmooth
      }
    }
  }
  
  return result
}

function calculateStochRSI(data: OHLCVData[], params: Record<string, any>): IndicatorResult {
  const rsiLength = params.rsiLength || 14
  const stochLength = params.stochLength || 14
  const kSmooth = params.kSmooth || 3
  const dSmooth = params.dSmooth || 3
  
  const rsi = calculateRSI(data, rsiLength)
  const result: IndicatorResult = []
  
  for (let i = 0; i < data.length; i++) {
    if (i < rsiLength + stochLength - 1) {
      result.push({ time: data[i].time })
      continue
    }
    
    let highest = -Infinity, lowest = Infinity
    for (let j = 0; j < stochLength; j++) {
      const val = rsi[i - j]?.value
      if (val !== undefined) {
        highest = Math.max(highest, val)
        lowest = Math.min(lowest, val)
      }
    }
    
    const rsiVal = rsi[i]?.value
    if (rsiVal !== undefined) {
      const range = highest - lowest
      const k = range > 0 ? ((rsiVal - lowest) / range) * 100 : 50
      result.push({ time: data[i].time, k, d: k, value: k })
    } else {
      result.push({ time: data[i].time })
    }
  }
  
  return result
}

function calculateCCI(data: OHLCVData[], period: number): IndicatorResult {
  const result: IndicatorResult = []
  
  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) {
      result.push({ time: data[i].time })
      continue
    }
    
    let sumTP = 0, sumMD = 0
    const typicalPrices: number[] = []
    
    for (let j = 0; j < period; j++) {
      const tp = (data[i - j].high + data[i - j].low + data[i - j].close) / 3
      typicalPrices.push(tp)
      sumTP += tp
    }
    
    const avgTP = sumTP / period
    for (const tp of typicalPrices) {
      sumMD += Math.abs(tp - avgTP)
    }
    const meanDev = sumMD / period
    
    const cci = meanDev > 0 ? (typicalPrices[0] - avgTP) / (0.015 * meanDev) : 0
    result.push({ time: data[i].time, value: cci })
  }
  
  return result
}

function calculateMFI(data: OHLCVData[], period: number): IndicatorResult {
  const result: IndicatorResult = []
  
  for (let i = 0; i < data.length; i++) {
    if (i < period) {
      result.push({ time: data[i].time })
      continue
    }
    
    let posFlow = 0, negFlow = 0
    for (let j = 0; j < period; j++) {
      const tp = (data[i - j].high + data[i - j].low + data[i - j].close) / 3
      const prevTP = j < period - 1 
        ? (data[i - j - 1].high + data[i - j - 1].low + data[i - j - 1].close) / 3
        : tp
      
      const rawMF = tp * data[i - j].volume
      if (tp > prevTP) posFlow += rawMF
      else negFlow += rawMF
    }
    
    const mfi = negFlow > 0 ? 100 - (100 / (1 + posFlow / negFlow)) : 100
    result.push({ time: data[i].time, value: mfi })
  }
  
  return result
}

function calculateWilliamsR(data: OHLCVData[], period: number): IndicatorResult {
  const result: IndicatorResult = []
  
  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) {
      result.push({ time: data[i].time })
      continue
    }
    
    let highest = -Infinity, lowest = Infinity
    for (let j = 0; j < period; j++) {
      highest = Math.max(highest, data[i - j].high)
      lowest = Math.min(lowest, data[i - j].low)
    }
    
    const range = highest - lowest
    const willR = range > 0 ? ((highest - data[i].close) / range) * -100 : -50
    result.push({ time: data[i].time, value: willR })
  }
  
  return result
}

function calculateROC(data: OHLCVData[], period: number): IndicatorResult {
  return data.map((d, i) => {
    if (i < period) return { time: d.time }
    const prevClose = data[i - period].close
    return { time: d.time, value: ((d.close - prevClose) / prevClose) * 100 }
  })
}

function calculateMomentum(data: OHLCVData[], period: number): IndicatorResult {
  return data.map((d, i) => {
    if (i < period) return { time: d.time }
    return { time: d.time, value: d.close - data[i - period].close }
  })
}

function calculateAwesomeOscillator(data: OHLCVData[], params: Record<string, any>): IndicatorResult {
  const fast = params.fastLength || 5
  const slow = params.slowLength || 34
  
  const medianData = data.map(d => ({
    ...d,
    close: (d.high + d.low) / 2
  }))
  
  const smaFast = calculateSMA(medianData, fast)
  const smaSlow = calculateSMA(medianData, slow)
  
  return data.map((d, i) => {
    const f = smaFast[i]?.value
    const s = smaSlow[i]?.value
    if (f !== undefined && s !== undefined) {
      const ao = f - s
      const prevAo = i > 0 ? (smaFast[i-1]?.value || 0) - (smaSlow[i-1]?.value || 0) : ao
      return { 
        time: d.time, 
        value: ao,
        color: ao >= prevAo ? '#00ff88' : '#ff3366'
      }
    }
    return { time: d.time }
  })
}

// === Volatility ===

function calculateBollingerBands(data: OHLCVData[], params: Record<string, any>): IndicatorResult {
  const period = params.length || 20
  const stdDev = params.stdDev || 2
  
  const sma = calculateSMA(data, period)
  const result: IndicatorResult = []
  
  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) {
      result.push({ time: data[i].time })
      continue
    }
    
    const middle = sma[i].value!
    let sumSq = 0
    for (let j = 0; j < period; j++) {
      sumSq += Math.pow(data[i - j].close - middle, 2)
    }
    const std = Math.sqrt(sumSq / period)
    
    result.push({
      time: data[i].time,
      upper: middle + stdDev * std,
      middle,
      lower: middle - stdDev * std,
      value: middle
    })
  }
  
  return result
}

function calculateATR(data: OHLCVData[], period: number): IndicatorResult {
  const result: IndicatorResult = []
  let atr = 0
  
  for (let i = 0; i < data.length; i++) {
    const tr = i === 0 
      ? data[i].high - data[i].low
      : Math.max(
          data[i].high - data[i].low,
          Math.abs(data[i].high - data[i - 1].close),
          Math.abs(data[i].low - data[i - 1].close)
        )
    
    if (i < period) {
      atr += tr
      if (i === period - 1) atr /= period
      result.push({ time: data[i].time })
    } else {
      atr = (atr * (period - 1) + tr) / period
      result.push({ time: data[i].time, value: atr })
    }
  }
  
  return result
}

function calculateKeltnerChannel(data: OHLCVData[], params: Record<string, any>): IndicatorResult {
  const period = params.length || 20
  const multiplier = params.atrMultiplier || 2
  
  const ema = calculateEMA(data, period)
  const atr = calculateATR(data, period)
  
  return data.map((d, i) => {
    const e = ema[i]?.value
    const a = atr[i]?.value
    if (e !== undefined && a !== undefined) {
      return {
        time: d.time,
        upper: e + multiplier * a,
        middle: e,
        lower: e - multiplier * a,
        value: e
      }
    }
    return { time: d.time }
  })
}

function calculateDonchianChannel(data: OHLCVData[], period: number): IndicatorResult {
  return data.map((d, i) => {
    if (i < period - 1) return { time: d.time }
    
    let highest = -Infinity, lowest = Infinity
    for (let j = 0; j < period; j++) {
      highest = Math.max(highest, data[i - j].high)
      lowest = Math.min(lowest, data[i - j].low)
    }
    
    return {
      time: d.time,
      upper: highest,
      middle: (highest + lowest) / 2,
      lower: lowest,
      value: (highest + lowest) / 2
    }
  })
}

// === Volume ===

function calculateVolume(data: OHLCVData[]): IndicatorResult {
  return data.map(d => ({ time: d.time, value: d.volume }))
}

function calculateVWAP(data: OHLCVData[]): IndicatorResult {
  let cumPV = 0, cumV = 0
  return data.map(d => {
    const tp = (d.high + d.low + d.close) / 3
    cumPV += tp * d.volume
    cumV += d.volume
    return { time: d.time, value: cumV > 0 ? cumPV / cumV : tp }
  })
}

function calculateOBV(data: OHLCVData[]): IndicatorResult {
  let obv = 0
  return data.map((d, i) => {
    if (i === 0) return { time: d.time, value: d.volume }
    
    if (d.close > data[i - 1].close) obv += d.volume
    else if (d.close < data[i - 1].close) obv -= d.volume
    
    return { time: d.time, value: obv }
  })
}

function calculateADL(data: OHLCVData[]): IndicatorResult {
  let adl = 0
  return data.map(d => {
    const range = d.high - d.low
    const mfm = range > 0 ? ((d.close - d.low) - (d.high - d.close)) / range : 0
    adl += mfm * d.volume
    return { time: d.time, value: adl }
  })
}

function calculateCMF(data: OHLCVData[], period: number): IndicatorResult {
  const result: IndicatorResult = []
  
  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) {
      result.push({ time: data[i].time })
      continue
    }
    
    let sumMFV = 0, sumV = 0
    for (let j = 0; j < period; j++) {
      const d = data[i - j]
      const range = d.high - d.low
      const mfm = range > 0 ? ((d.close - d.low) - (d.high - d.close)) / range : 0
      sumMFV += mfm * d.volume
      sumV += d.volume
    }
    
    result.push({ time: data[i].time, value: sumV > 0 ? sumMFV / sumV : 0 })
  }
  
  return result
}

// === Trend ===

function calculateADX(data: OHLCVData[], period: number): IndicatorResult {
  const dmi = calculateDMI(data, period)
  const result: IndicatorResult = []
  let adx = 0
  
  for (let i = 0; i < data.length; i++) {
    if (i < period * 2 - 1) {
      result.push({ time: data[i].time })
      continue
    }
    
    const plusDI = dmi[i]?.plusDI || 0
    const minusDI = dmi[i]?.minusDI || 0
    const dx = (plusDI + minusDI) > 0 ? Math.abs(plusDI - minusDI) / (plusDI + minusDI) * 100 : 0
    
    if (i === period * 2 - 1) {
      adx = dx
    } else {
      adx = (adx * (period - 1) + dx) / period
    }
    
    result.push({ time: data[i].time, value: adx, plusDI, minusDI })
  }
  
  return result
}

function calculateDMI(data: OHLCVData[], period: number): IndicatorResult {
  const result: IndicatorResult = []
  let smoothedPlusDM = 0, smoothedMinusDM = 0, smoothedTR = 0
  
  for (let i = 0; i < data.length; i++) {
    if (i === 0) {
      result.push({ time: data[i].time })
      continue
    }
    
    const upMove = data[i].high - data[i - 1].high
    const downMove = data[i - 1].low - data[i].low
    
    const plusDM = upMove > downMove && upMove > 0 ? upMove : 0
    const minusDM = downMove > upMove && downMove > 0 ? downMove : 0
    
    const tr = Math.max(
      data[i].high - data[i].low,
      Math.abs(data[i].high - data[i - 1].close),
      Math.abs(data[i].low - data[i - 1].close)
    )
    
    if (i < period) {
      smoothedPlusDM += plusDM
      smoothedMinusDM += minusDM
      smoothedTR += tr
      result.push({ time: data[i].time })
    } else if (i === period) {
      smoothedPlusDM = smoothedPlusDM - (smoothedPlusDM / period) + plusDM
      smoothedMinusDM = smoothedMinusDM - (smoothedMinusDM / period) + minusDM
      smoothedTR = smoothedTR - (smoothedTR / period) + tr
      
      const plusDI = smoothedTR > 0 ? (smoothedPlusDM / smoothedTR) * 100 : 0
      const minusDI = smoothedTR > 0 ? (smoothedMinusDM / smoothedTR) * 100 : 0
      
      result.push({ time: data[i].time, plusDI, minusDI, value: plusDI })
    } else {
      smoothedPlusDM = smoothedPlusDM - (smoothedPlusDM / period) + plusDM
      smoothedMinusDM = smoothedMinusDM - (smoothedMinusDM / period) + minusDM
      smoothedTR = smoothedTR - (smoothedTR / period) + tr
      
      const plusDI = smoothedTR > 0 ? (smoothedPlusDM / smoothedTR) * 100 : 0
      const minusDI = smoothedTR > 0 ? (smoothedMinusDM / smoothedTR) * 100 : 0
      
      result.push({ time: data[i].time, plusDI, minusDI, value: plusDI })
    }
  }
  
  return result
}

function calculateParabolicSAR(data: OHLCVData[], params: Record<string, any>): IndicatorResult {
  const start = params.start || 0.02
  const increment = params.increment || 0.02
  const maximum = params.maximum || 0.2
  
  const result: IndicatorResult = []
  let isUptrend = true
  let sar = data[0]?.low || 0
  let ep = data[0]?.high || 0
  let af = start
  
  for (let i = 0; i < data.length; i++) {
    if (i < 2) {
      result.push({ time: data[i].time })
      continue
    }
    
    const prevSar = sar
    sar = prevSar + af * (ep - prevSar)
    
    if (isUptrend) {
      sar = Math.min(sar, data[i - 1].low, data[i - 2].low)
      if (data[i].low < sar) {
        isUptrend = false
        sar = ep
        ep = data[i].low
        af = start
      } else {
        if (data[i].high > ep) {
          ep = data[i].high
          af = Math.min(af + increment, maximum)
        }
      }
    } else {
      sar = Math.max(sar, data[i - 1].high, data[i - 2].high)
      if (data[i].high > sar) {
        isUptrend = true
        sar = ep
        ep = data[i].high
        af = start
      } else {
        if (data[i].low < ep) {
          ep = data[i].low
          af = Math.min(af + increment, maximum)
        }
      }
    }
    
    result.push({ time: data[i].time, value: sar, isUptrend })
  }
  
  return result
}

function calculateSuperTrend(data: OHLCVData[], params: Record<string, any>): IndicatorResult {
  const period = params.length || 10
  const multiplier = params.multiplier || 3
  
  const atr = calculateATR(data, period)
  const result: IndicatorResult = []
  let upperBand = 0, lowerBand = 0
  let superTrend = 0
  let direction = 1
  
  for (let i = 0; i < data.length; i++) {
    if (i < period) {
      result.push({ time: data[i].time })
      continue
    }
    
    const hl2 = (data[i].high + data[i].low) / 2
    const atrVal = atr[i]?.value || 0
    
    const basicUpperBand = hl2 + multiplier * atrVal
    const basicLowerBand = hl2 - multiplier * atrVal
    
    upperBand = basicUpperBand < upperBand || data[i - 1].close > upperBand 
      ? basicUpperBand : upperBand
    lowerBand = basicLowerBand > lowerBand || data[i - 1].close < lowerBand 
      ? basicLowerBand : lowerBand
    
    if (direction === 1) {
      if (data[i].close < lowerBand) {
        direction = -1
        superTrend = upperBand
      } else {
        superTrend = lowerBand
      }
    } else {
      if (data[i].close > upperBand) {
        direction = 1
        superTrend = lowerBand
      } else {
        superTrend = upperBand
      }
    }
    
    result.push({ 
      time: data[i].time, 
      value: superTrend,
      direction,
      color: direction === 1 ? '#00ff88' : '#ff3366'
    })
  }
  
  return result
}

function calculateIchimoku(data: OHLCVData[], params: Record<string, any>): IndicatorResult {
  const conversion = params.conversionPeriod || 9
  const base = params.basePeriod || 26
  const span = params.spanPeriod || 52
  const displacement = params.displacement || 26
  
  const result: IndicatorResult = []
  
  const highLow = (period: number, index: number) => {
    let highest = -Infinity, lowest = Infinity
    for (let j = 0; j < period && index - j >= 0; j++) {
      highest = Math.max(highest, data[index - j].high)
      lowest = Math.min(lowest, data[index - j].low)
    }
    return (highest + lowest) / 2
  }
  
  for (let i = 0; i < data.length; i++) {
    const tenkan = highLow(conversion, i)
    const kijun = highLow(base, i)
    const senkouA = (tenkan + kijun) / 2
    const senkouB = highLow(span, i)
    const chikou = data[i].close
    
    result.push({
      time: data[i].time,
      tenkan,
      kijun,
      senkouA,
      senkouB,
      chikou,
      value: tenkan
    })
  }
  
  return result
}

function calculateAroon(data: OHLCVData[], period: number): IndicatorResult {
  return data.map((d, i) => {
    if (i < period) return { time: d.time }
    
    let highIdx = 0, lowIdx = 0
    let highest = -Infinity, lowest = Infinity
    
    for (let j = 0; j <= period; j++) {
      if (data[i - j].high >= highest) {
        highest = data[i - j].high
        highIdx = j
      }
      if (data[i - j].low <= lowest) {
        lowest = data[i - j].low
        lowIdx = j
      }
    }
    
    const aroonUp = ((period - highIdx) / period) * 100
    const aroonDown = ((period - lowIdx) / period) * 100
    
    return { time: d.time, aroonUp, aroonDown, value: aroonUp }
  })
}

// === Support/Resistance ===

function calculatePivots(data: OHLCVData[]): IndicatorResult {
  if (data.length === 0) return []
  
  const lastBar = data[data.length - 1]
  const pivot = (lastBar.high + lastBar.low + lastBar.close) / 3
  const r1 = 2 * pivot - lastBar.low
  const s1 = 2 * pivot - lastBar.high
  const r2 = pivot + (lastBar.high - lastBar.low)
  const s2 = pivot - (lastBar.high - lastBar.low)
  const r3 = lastBar.high + 2 * (pivot - lastBar.low)
  const s3 = lastBar.low - 2 * (lastBar.high - pivot)
  
  return data.map(d => ({
    time: d.time,
    pivot,
    r1, r2, r3,
    s1, s2, s3,
    value: pivot
  }))
}

function calculateAutoFib(data: OHLCVData[], period: number): IndicatorResult {
  const result: IndicatorResult = []
  
  for (let i = 0; i < data.length; i++) {
    if (i < period) {
      result.push({ time: data[i].time })
      continue
    }
    
    let highest = -Infinity, lowest = Infinity
    for (let j = 0; j < period; j++) {
      highest = Math.max(highest, data[i - j].high)
      lowest = Math.min(lowest, data[i - j].low)
    }
    
    const range = highest - lowest
    result.push({
      time: data[i].time,
      level0: lowest,
      level236: lowest + range * 0.236,
      level382: lowest + range * 0.382,
      level500: lowest + range * 0.5,
      level618: lowest + range * 0.618,
      level786: lowest + range * 0.786,
      level1000: highest,
      value: lowest + range * 0.5
    })
  }
  
  return result
}

export {
  calculateSMA,
  calculateEMA,
  calculateWMA,
  calculateRSI,
  calculateMACD,
  calculateBollingerBands,
  calculateATR,
  calculateVWAP,
}
